// Shared text search/matcher utilities.
//
// Supports:
//   * = any number of characters (wildcard)
//   ? = exactly one character (wildcard)
//   | = OR operator between alternative terms (e.g. "foo|bar")
//
// Whitespace around each '|'-separated segment is trimmed. Empty segments
// (e.g. from "foo||bar" or a trailing pipe) are ignored.

// User-facing description of the supported search syntax. Re-use this in any
// input tooltip/help text so the wording stays consistent.
export const SEARCH_SYNTAX_HELP =
  "Use '?' to search for a word or phrase where one character can vary, e.g. 'MP?N'.\n" +
  "Use '*' to search for a word or phrase where any number of characters can vary, e.g. 'Set*Tariff'.\n" +
  "Use '|' to search for any one of several words or phrases, e.g. 'This|That'.";
export function escapeRegex(value) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function buildSegmentMatcher(segment) {
  const trimmed = segment.trim();
  if (!trimmed) return null;

  const hasWildcards = trimmed.includes('*') || trimmed.includes('?');

  if (!hasWildcards) {
    const lowered = trimmed.toLowerCase();
    return (text) => text.toLowerCase().includes(lowered);
  }

  const pattern = escapeRegex(trimmed)
    .replace(/\\\*/g, '.*')
    .replace(/\\\?/g, '.');

  const regex = new RegExp(pattern, 'i');
  return (text) => regex.test(text);
}

/**
 * Build a matcher function for the given search term.
 *
 * @param {string} term  The search expression (may contain *, ?, |).
 * @returns {(text: string) => boolean}
 */
export function buildSearchMatcher(term) {
  if (term == null) return () => true;
  const raw = String(term);
  if (!raw.trim()) return () => true;

  const segmentMatchers = raw
    .split('|')
    .map((segment) => buildSegmentMatcher(segment))
    .filter(Boolean);

  if (segmentMatchers.length === 0) return () => true;

  return (text = '') => {
    const str = text == null ? '' : String(text);
    return segmentMatchers.some((m) => m(str));
  };
}
